import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './GameDetailModal.module.css';

const formatPct = (pct) => (pct == null ? '—' : `${pct}%`);

const SplitSide = ({ side, align = 'left' }) => {
  if (!side) return null;
  const pctClass = side.pct == null
    ? styles.splitPctEmpty
    : side.pct >= 55
      ? styles.splitPctGood
      : side.pct <= 45
        ? styles.splitPctBad
        : styles.splitPctNeutral;

  return (
    <div className={`${styles.splitSide} ${align === 'right' ? styles.splitSideRight : styles.splitSideLeft}`}>
      <div className={styles.splitCondition}>{side.label}</div>
      <div className={styles.splitStats}>
        <span className={pctClass}>{formatPct(side.pct)}</span>
        <span className={styles.splitRecord}>{side.recordLabel || '0-0'}</span>
      </div>
    </div>
  );
};

SplitSide.propTypes = {
  side: PropTypes.object,
  align: PropTypes.oneOf(['left', 'right']),
};

const GameDetailSplits = ({ game, matchupStats, loading, error }) => {
  const [mode, setMode] = useState('spread');

  const rows = mode === 'spread'
    ? (matchupStats?.splits?.spread || [])
    : (matchupStats?.splits?.overUnder || []);

  return (
    <div className={styles.splitsSection}>
      <div className={styles.splitsHeader}>
        <div className={styles.splitsModeToggle} role="tablist" aria-label="Split type">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'spread'}
            className={`${styles.splitsModeButton} ${mode === 'spread' ? styles.splitsModeActive : ''}`}
            onClick={() => setMode('spread')}
          >
            Spread
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'overUnder'}
            className={`${styles.splitsModeButton} ${mode === 'overUnder' ? styles.splitsModeActive : ''}`}
            onClick={() => setMode('overUnder')}
          >
            Over / Under
          </button>
        </div>
      </div>

      <div className={styles.splitsTeamHeaders}>
        <div className={styles.splitsTeamHeaderLeft}>
          {game.away_team.logos?.[0] && (
            <img src={game.away_team.logos[0]} alt="" className={styles.splitsTeamLogo} />
          )}
          <span>{game.away_team.abbreviation || game.away_team.name}</span>
        </div>
        <div className={styles.splitsTeamHeaderRight}>
          <span>{game.home_team.abbreviation || game.home_team.name}</span>
          {game.home_team.logos?.[0] && (
            <img src={game.home_team.logos[0]} alt="" className={styles.splitsTeamLogo} />
          )}
        </div>
      </div>

      {loading && <p className={styles.gameLogMessage}>Loading season splits…</p>}
      {!loading && error && <p className={styles.gameLogMessage}>{error}</p>}

      {!loading && !error && rows.length === 0 && (
        <p className={styles.gameLogMessage}>No completed games to build splits yet.</p>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className={styles.splitsTable}>
          {rows.map((row) => (
            <div key={row.key} className={styles.splitsRow}>
              <SplitSide side={row.away} align="left" />
              <div className={styles.splitsCategory}>{row.category}</div>
              <SplitSide side={row.home} align="right" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

GameDetailSplits.propTypes = {
  game: PropTypes.object.isRequired,
  matchupStats: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export default GameDetailSplits;
